import {
  AptChannel,
  BookType,
  MODES_OF_APT,
  QUEUE_TYPE,
  SupportedTeleProviders,
} from "@elixir/types";
import { defaultQueueFilters } from "@elixir/utils";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import getTimeOfDaysApplicable from "../../utils/get-time-of-day";
import { QueueStore } from "./types";
import {
  changeAptListenerClinic,
  changeAptListenerDate,
  changeAptListenerDoctor,
} from "../../utils/firestore-utils/apt-listener-manager";

const useQueueStore = create<QueueStore>()(
  persist(
    (set, get) => ({
      isSyncing: true,
      setIsSyncing: (isSyncing) => {
        set({ isSyncing });
      },
      isNetworkOnline: true,
      setIsNetworkOnline: (isNetworkOnline) => {
        set({ isNetworkOnline });
      },

      mode: MODES_OF_APT.IN_CLINIC,
      setMode: (mode) =>
        set({ mode, vcMeta: { platform: SupportedTeleProviders.eka } }),
      showAddMedicalHistoryPopup: false,
      setShowAddMedicalHistoryPopup: (status) =>
        set({ showAddMedicalHistoryPopup: status }),
      selectedSort: undefined,
      setSelectedSort: (sort) => set({ selectedSort: sort }),
      searchQuery: "",
      setSearchQuery: (query) => set({ searchQuery: query }),
      leftSearchQuery: "",
      setLeftSearchQuery: (query) => set({ leftSearchQuery: query }),
      rescheduleAppointment: undefined,
      setRescheduleAppointment: (appointment) =>
        set({ rescheduleAppointment: appointment }),
      openLeftPanel: true,
      setOpenLeftPanel: (open = true) => set({ openLeftPanel: open }),
      clinicId: "",
      selectedAppointment: undefined,
      setSelectedAppointment: (appointment) =>
        set({ selectedAppointment: appointment }),
      setClinicId: (clinicId) => {
        set({ clinicId });
        const { clinicId: oldClinicId } = get();

        if (oldClinicId) {
          //old clinic id exists, this is an update
          changeAptListenerClinic(clinicId);
        }
      },
      viewType: "default",
      setViewType: (viewType) => set({ viewType }),
      showCustomiseYourQueue: false,
      setShowCustomiseYourQueue: (show) =>
        set({ showCustomiseYourQueue: show }),
      rightPanelQueueType: QUEUE_TYPE.CHECKED_IN,
      leftPanelQueueType: "add-new",
      setRightPanelQueueType: (queueType) =>
        set({ rightPanelQueueType: queueType }),
      setLeftPanelQueueType: (queueType) =>
        set({ leftPanelQueueType: queueType }),
      selectedDate: new Date(),
      customSlotDate: new Date(),
      customSelectedDaySlot: "today",
      selectedAppointments: [],
      customSlotDuration: 15,
      timeOfDayForCustomSlot: getTimeOfDaysApplicable(new Date()),
      selectedSlot: undefined,
      setSelectedDate: (date) => {
        set({ selectedDate: date });
        changeAptListenerDate(date);
      },
      setCustomSlotDuration: (duration) =>
        set({ customSlotDuration: duration }),
      setCustomSlotDate: (date) => set({ customSlotDate: date }),
      setCustomSelectedDaySlot: (daySlot) =>
        set({ customSelectedDaySlot: daySlot }),
      selectAppointments: (appointments) => {
        set({ selectedAppointments: appointments });
      },
      updateSelectedAppointment: (aptResponse) => {
        const { selectAppointments, selectedAppointments } = get();
        if (selectedAppointments.length === 1) {
          const [selectedApt] = selectedAppointments;

          if (selectedApt) {
            const {
              bookedAppointments = [],
              followupAppointments = [],
              otherAppointments = [],
            } = aptResponse;

            const updatedApt = [
              ...bookedAppointments,
              ...followupAppointments,
              ...otherAppointments,
            ].find((apt) => selectedApt.aid === apt.aid);

            if (updatedApt) {
              selectAppointments([updatedApt]);
            }
          }
        }
      },
      removeAppointment: (appointmentId) => {
        const { selectedAppointments } = get();
        const indexOfAptToBeRemoved = selectedAppointments.findIndex(
          (apt) => apt.aid === appointmentId
        );

        set({
          selectedAppointments: [
            ...selectedAppointments.slice(0, indexOfAptToBeRemoved),
            ...selectedAppointments.slice(indexOfAptToBeRemoved + 1),
          ],
        });
      },
      clearSelectedAppointments: () => set({ selectedAppointments: [] }),
      setTimeOfDayForCustomSlot: (timeOfDay) =>
        set({ timeOfDayForCustomSlot: timeOfDay }),
      selectSlot: (slot) => set({ selectedSlot: slot }),
      clearSelectedSlot: () => set({ selectedSlot: undefined }),
      setSelectedPatient: (patient) => set({ selectedPatient: patient }),
      clearSelectedPatient: () => set({ selectedPatient: undefined }),
      clearStatusOnAddAptClose: () =>
        set({
          selectedSlot: undefined,
          customSlotDuration: 15,
          customSlotDate: new Date(),
          selectedPatient: undefined,
          selectedAppointments: [],
          vcMeta: {
            platform: SupportedTeleProviders.eka,
          },
        }),
      clearStatusOnAptBottomsheetClose: () =>
        set({
          selectedPatient: undefined,
          selectedAppointments: [],
        }),
      tabNavigation: undefined,
      setTabNavigation: (tabNavigation) => set({ tabNavigation }),
      snackBar: {
        message: "",
      },
      setSnackBar: ({ message, duration }) => {
        set({
          snackBar: {
            message,
            duration,
          },
        });
      },

      queueFilter: defaultQueueFilters,
      setQueueFilter: (newQueueFilter) => set({ queueFilter: newQueueFilter }),
      leftQueueFilter: defaultQueueFilters,
      setLeftQueueFilter: (newQueueFilter) =>
        set({ leftQueueFilter: newQueueFilter }),
      clearQueueFilter: () => set({ queueFilter: defaultQueueFilters }),
      showSelectSlot: false,
      setShowSelectSlot: (status) => set({ showSelectSlot: status }),
      setScrollToValues: ({ aid, queueType }) => {
        set({
          scrollToAid: aid,
          scrollToQueue: queueType,
        });
      },
      clearScrollToValues: () => {
        set({ scrollToAid: undefined, scrollToQueue: undefined });
      },
      showRequestedAptPopup: false,
      setShowRequestedAptPopup: (showRequestedAptPopup) =>
        set({ showRequestedAptPopup }),
      showAppointmentDetailsDrawer: false,
      setShowAppointmentDetailsDrawer: (showAppointmentDetailsDrawer) =>
        set({ showAppointmentDetailsDrawer }),
      showSendReminderModal: false,
      setShowSendReminderModal: (showSendReminderModal) =>
        set({ showSendReminderModal }),
      showSelectSlotModal: false,
      setShowSelectSlotModal: (showSelectSlotModal) =>
        set({ showSelectSlotModal }),
      selectSlotBookType: undefined,
      selectSlotAptChannel: undefined,
      setSelectSlotInfo: ({
        bookType,
        aptChannel,
        showModal,
      }: {
        bookType: BookType | undefined;
        aptChannel: AptChannel | undefined;
        showModal: boolean;
      }) =>
        set({
          selectSlotBookType: bookType,
          selectSlotAptChannel: aptChannel,
          showSelectSlotModal: showModal,
        }),

      setSelectedDoctor: (selectedDoctor) => {
        set({ selectedDoctor });

        const { isAllDoctorsHidden } = get();
        if (isAllDoctorsHidden() && selectedDoctor) {
          //old doctor exists, this is an update
          changeAptListenerDoctor([selectedDoctor?.oid]);
        } else {
          const { getMyDotorIds } = get();
          changeAptListenerDoctor(getMyDotorIds());
        }
      },

      getMyDotorIds: () => {
        const { clinicId } = get();
        //@ts-ignore
        const myDoctors = new Bridge().getMe()?.docs;
        if (clinicId === "all") {
          return myDoctors?.map((doc) => doc.id) || [];
        } else {
          return myDoctors?.filter((doc) => doc.professional.clinics.find((t) => t._id === clinicId)).map((doc) => doc.id) || [];
        }
      },
      setCalendarConfigs: (calendarConfigs) => {
        set({ calendarConfigs });
      },
      setEvitalsConfig: (evitalsConfig) => {
        set({ evitalsConfig });
      },
      setOpenQuestionnaireDrawer: (open) => {
        set({ openQuestionnaireDrawer: open });
      },
      setIsQuestionnaireDrawerOpenedFromPad: (status) => {
        set({ isQuestionnaireDrawerOpenedFromPad: status });
      },
      setQuestionnaireWorkflowId: (questionnaireWorkflowId) => {
        set({ questionnaireWorkflowId });
      },
      clearQuestionnaireWorkflowId: () => {
        set({ questionnaireWorkflowId: undefined });
      },

      setWorkflowIdInShowResponse: (workflowIdInShowResponse) => {
        set({ workflowIdInShowResponse });
      },

      showAssessmentIds: [],
      setShowAssessmentIds: (assessmentIds) => {
        set({ showAssessmentIds: assessmentIds });
      },

      clearShowResponseState: () => {
        set({ workflowIdInShowResponse: undefined, showAssessmentIds: [] });
      },

      setFetchBusinessUsers: (fetchBusinessUsers) => {
        set({ fetchBusinessUsers });
      },

      vcMeta: {
        platform: SupportedTeleProviders.eka,
      },

      setVcMeta: (vcMeta) => {
        set({ vcMeta });
      },

      isAllDoctorsHidden: () => {
        const { getMyDotorIds, clinicId } = get();
        if (clinicId === "all") {
          return getMyDotorIds().length > 30;
        }
        return getMyDotorIds().length + 1 > 30;
        // return true;
      },
    }),

    {
      name: "local-queue-store",
      partialize: (state) => {
        return {
          openLeftPanel: state.openLeftPanel,
          viewType: state.viewType,
          selectedSort: state.selectedSort,
        };
      },
    }
  )
);

export default useQueueStore;
